import { PassphraseInput } from "./PassphraseInput";
import { PassphraseGuide } from "./PassphraseGuide";
import { PasswordStrength } from "./PasswordStrength";

const MIN_WORDS = 4;

type PasswordMode = "passphrase" | "classic";

export function PasswordField({
  passwordMode,
  onPasswordModeChange,
  password,
  onPasswordChange,
  passphraseWords,
  onPassphraseWordsChange,
  showPassword,
  onShowPasswordChange,
  isRegister,
  onForgot,
}: {
  readonly passwordMode: PasswordMode;
  readonly onPasswordModeChange: (mode: PasswordMode) => void;
  readonly password: string;
  readonly onPasswordChange: (password: string) => void;
  readonly passphraseWords: string[];
  readonly onPassphraseWordsChange: (words: string[]) => void;
  readonly showPassword: boolean;
  readonly onShowPasswordChange: (show: boolean) => void;
  readonly isRegister: boolean;
  readonly onForgot?: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => { onPasswordModeChange("passphrase"); onPasswordChange(""); }}
            className={`flex-1 px-3 py-2 text-xs transition-colors ${
              passwordMode === "passphrase"
                ? "bg-accent text-white"
                : "bg-surface-alt text-ink-muted hover:text-ink"
            }`}
          >
            Phrase de passe
          </button>
          <button
            type="button"
            onClick={() => { onPasswordModeChange("classic"); onPassphraseWordsChange([]); }}
            className={`flex-1 px-3 py-2 text-xs transition-colors ${
              passwordMode === "classic"
                ? "bg-accent text-white"
                : "bg-surface-alt text-ink-muted hover:text-ink"
            }`}
          >
            Mot de passe
          </button>
        </div>
        {passwordMode === "passphrase" && (
          <p className="mt-1.5 text-[10px] text-ink-faint leading-snug">
            Recommandé par le{" "}
            <a
              href="https://pages.nist.gov/800-63-3/sp800-63b.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-ink-muted"
            >
              NIST
            </a>
            {" "}— plus sécurisé et plus facile à retenir qu'un mot de passe classique.
          </p>
        )}
      </div>

      {passwordMode === "passphrase" ? (
        <>
          <PassphraseInput
            words={passphraseWords}
            onChange={onPassphraseWordsChange}
            minWords={MIN_WORDS}
            placeholder="Tapez un mot puis Espace..."
            autoComplete={isRegister ? "new-password" : "current-password"}
          />
          {isRegister && (
            <PassphraseGuide
              wordCount={passphraseWords.length}
              onGenerate={onPassphraseWordsChange}
            />
          )}
        </>
      ) : (
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder={isRegister ? "Mot de passe (min 12 caractères)" : "Votre mot de passe"}
            required
            autoComplete={isRegister ? "new-password" : "current-password"}
            className="w-full px-4 py-3 pr-16 bg-surface-alt border border-border text-ink
              placeholder:text-ink-muted focus:outline-none focus:ring-2
              focus:ring-accent rounded"
          />
          <button
            type="button"
            onClick={() => onShowPasswordChange(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted
              hover:text-ink text-sm select-none"
            tabIndex={-1}
          >
            {showPassword ? "Masquer" : "Voir"}
          </button>
        </div>
      )}

      {passwordMode === "classic" && isRegister && password.length > 0 && (
        <PasswordStrength length={password.length} />
      )}

      {onForgot && !isRegister && (
        <button
          type="button"
          onClick={onForgot}
          className="text-xs text-accent hover:underline self-start"
        >
          Mot de passe oublié ?
        </button>
      )}
    </div>
  );
}
